@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import app.vercors.website.components.layouts.BlogPostLayout as _app_vercors_website_components_layouts_BlogPostLayout
import app.vercors.website.components.layouts.MarkdownLayout as _app_vercors_website_components_layouts_MarkdownLayout
import app.vercors.website.components.layouts.PageLayout as _app_vercors_website_components_layouts_PageLayout
import app.vercors.website.pages.AboutPage as _app_vercors_website_pages_AboutPage
import app.vercors.website.pages.HomePage as _app_vercors_website_pages_HomePage
import app.vercors.website.pages.blog.BlogPage as _app_vercors_website_pages_blog_BlogPage
import app.vercors.website.pages.blog.WelcomePage as _app_vercors_website_pages_blog_WelcomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Vercors"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "app.vercors.website.components.layouts.BlogPostLayout",
            parentLayoutId = "app.vercors.website.components.layouts.MarkdownLayout",
        ) { pageCtx, pageMethod -> 
            _app_vercors_website_components_layouts_BlogPostLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "app.vercors.website.components.layouts.MarkdownLayout",
            parentLayoutId = "app.vercors.website.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                app.vercors.website.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _app_vercors_website_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "app.vercors.website.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _app_vercors_website_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "app.vercors.website.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                app.vercors.website.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _app_vercors_website_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "app.vercors.website.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                app.vercors.website.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _app_vercors_website_pages_AboutPage()
        }
        ctx.router.register(
            "/blog/",
            layoutId = "app.vercors.website.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                app.vercors.website.pages.blog.initBlogPage(ctx)
            },
        ) { pageCtx -> 
            _app_vercors_website_pages_blog_BlogPage()
        }
        ctx.router.register(
            "/blog/welcome",
            layoutId = "app.vercors.website.components.layouts.BlogPostLayout",
            initRouteMethod = { ctx ->
                app.vercors.website.pages.blog.initWelcomePage(ctx)
            },
        ) { pageCtx -> 
            _app_vercors_website_pages_blog_WelcomePage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("showcase-row", app.vercors.website.components.sections.ShowcaseRowStyle)
        ctx.theme.registerStyle("showcase-row-reversed", app.vercors.website.components.sections.ShowcaseRowReversedStyle)
        ctx.theme.registerStyle("nav-header", app.vercors.website.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("footer", app.vercors.website.components.sections.FooterStyle)
        ctx.theme.registerStyle("live-demo", app.vercors.website.components.widgets.LiveDemoStyle)
        ctx.theme.registerStyle("page-content", app.vercors.website.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("markdown", app.vercors.website.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("headline-text", app.vercors.website.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", app.vercors.website.SubheadlineTextStyle)
        ctx.theme.registerStyle("section-title", app.vercors.website.SectionTitleStyle)
        ctx.theme.registerStyle("card", app.vercors.website.CardStyle)
        ctx.theme.registerStyle("screenshot", app.vercors.website.ScreenshotStyle)
        ctx.theme.registerStyle("light-mode-fade", app.vercors.website.LightModeFadeStyle)
        ctx.theme.registerStyle("dark-mode-fade", app.vercors.website.DarkModeFadeStyle)
        ctx.theme.registerStyle("light-mode-only", app.vercors.website.LightModeOnlyStyle)
        ctx.theme.registerStyle("dark-mode-only", app.vercors.website.DarkModeOnlyStyle)
        ctx.theme.registerVariant("-primary", app.vercors.website.PrimaryButtonVariant)
        ctx.theme.registerVariant("-circle", app.vercors.website.CircleButtonVariant)
        ctx.theme.registerVariant("-uncolored", app.vercors.website.UncoloredButtonVariant)
        ctx.theme.registerKeyframes("side-menu-slide-in", app.vercors.website.components.sections.SideMenuSlideInAnim)
        app.vercors.website.initSiteStyles(ctx)
        app.vercors.website.initTheme(ctx)
        app.vercors.website.initColorMode(ctx)
        app.vercors.website.initStyles(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        app.vercors.website.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
